import clsx from 'clsx'
import {buildMaterijalPayload, validateMaterijal} from '../steps/materijalPayload'
import {useFormik} from 'formik'
import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react'
import useDebounce from '../../../../hooks/useDebounce'
import {sredstvoInfo} from '../../../../api/fmkNalozi'
import notifyToast from '../../../../components/Toast/Toast'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import RadniNaloziOdjeliDropdown from '../../../../components/RadniNaloziOdjeliDropdown/RadniNaloziOdjeliDropdown'
import {startRadnogNaloga} from '../../../../api/radniNalozi'
import {FmkNalogiStavkeModel} from '../../../../models/FmkNaloziModel'
import * as Yup from 'yup'
import {useNavigate} from 'react-router-dom'
import UposlenikDropdown from '../../../../components/UposlenikDropdown/UposlenikDropdown'
import {KTIcon} from '../../../../../_websoft/helpers'

interface Props {
    nalogaId: string
    selectedStavka: FmkNalogiStavkeModel['stavka']
    materijal?: any
}
interface SredstvoInfoModel {
    SredstvoNaziv: string
}
export type Ref = {
    validateData: () => Promise<boolean>
    saveData: () => Promise<void>
}

const getApiErrorMessage = (error: any, fallback = 'Greška. Pokušajte ponovo.') => {
    return (
        error?.response?.data?.message ||
        error?.message ||
        fallback
    )
}

const RadniNalogComponent = forwardRef<Ref, Props>(({nalogaId, selectedStavka, materijal}, ref) => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [sredstvoInfoData, setsredstvoInfoData] = useState<SredstvoInfoModel | null>(null)

    const initialValues = {
        invbroj: '',
        Odjel: '',
        Izvrsilac: [''],
    }

    const validationSchema = Yup.object({
        invbroj: Yup.string().trim().required('Upišite inventurni broj mašine.'),
        Odjel: Yup.string().trim().required('Odaberite odjel.'),
    })

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (values) => saveData(values),
    })

    const validateData = async () => {
        const errors = await formik.validateForm()
        await formik.setTouched({invbroj: true, Odjel: true}, false)
        if (Object.keys(errors).length) {
            notifyToast(false, Object.values(errors).join(' '))
            return false
        }
        return true
    }

    useImperativeHandle(ref, () => ({
        validateData,
        saveData: async () => {
            if (await validateData()) await formik.submitForm()
        },
    }))

    const saveData = (values: typeof initialValues) => {
        const error = validateMaterijal(materijal, selectedStavka)
        if (error) {
            notifyToast(false, error)
            return
        }
        setLoading(true)

        const materijalPayload = buildMaterijalPayload(materijal, selectedStavka)
        const payloadData = {
            BrojNaloga: nalogaId,
            MasinaId: values.invbroj,
            OdjelId: values.Odjel,
            stavke: materijalPayload.stavke,
            materijal: materijalPayload,
            izvrsilac: values.Izvrsilac,
        }

        return startRadnogNaloga(payloadData)
            .then((response: any) => {
                notifyToast(true, response.message)
                setTimeout(() => navigate(-1), 3000)
            })
            .catch((error: any) => {
                notifyToast(false, getApiErrorMessage(error))
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const debouncedInv = useDebounce(formik.values.invbroj)

    useEffect(() => {
        // clear label when user clears input
        if (!debouncedInv) {
            setsredstvoInfoData(null)
            return
        }

        sredstvoInfo({invBroj: debouncedInv})
            .then((response: any) => {
                setsredstvoInfoData(response ?? null)
            })
            .catch((error: any) => {
                // ✅ prevents crash + avoids annoying toast spam
                notifyToast(false, getApiErrorMessage(error))
                setsredstvoInfoData(null)
            })
    }, [debouncedInv])

    return (
        <div className='w-100'>
            {/* Header */}
            <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-6'>
                <div>
                    <h2 className='fw-bolder text-dark mb-1'>Radni Nalog</h2>
                    <div className='text-muted'>
                        Broj naloga: <span className='fw-semibold text-dark'>{nalogaId}</span>
                    </div>
                </div>

                {/* subtle helper badge */}
                <div className='d-flex align-items-center gap-2'>
                    <span className='badge badge-light-primary py-2 px-3'>Unos radnog naloga</span>
                </div>
            </div>

            {loading ? (
                <div className='card'>
                    <div className='card-body p-6'>
                        <LoadingComponent />
                    </div>
                </div>
            ) : (
                <div>
                    <div className='card shadow-sm'>
                        <div className='card-body p-4 p-md-6'>
                            {/* Top fields */}
                            <div className='row g-4'>
                                {/* Odjel */}
                                <div className='col-12 col-lg-4'>
                                    <label className='form-label fw-bold mb-2'>Odjel</label>
                                    <div className=''>
                                        <RadniNaloziOdjeliDropdown
                                            value={formik.values.Odjel}
                                            onChange={(name, value) => formik.setFieldValue('Odjel', value)}
                                            selectName='Odjel'
                                            errors={formik.errors.Odjel}
                                            touched={formik.touched.Odjel}
                                        />
                                    </div>
                                </div>

                                {/* Inv broj */}
                                <div className='col-12 col-lg-8'>
                                    <label className='form-label fw-bold mb-2'>Inv. broj mašine</label>

                                    <div className='input-group input-group-lg'>
                    <span className='input-group-text bg-light'>
                      <KTIcon iconName='barcode' className='fs-2' />
                    </span>

                                        <input
                                            type='text'
                                            placeholder='npr. 12345'
                                            {...formik.getFieldProps('invbroj')}
                                            className={clsx(
                                                'form-control form-control-lg form-control-solid',
                                                {'is-invalid': formik.touched.invbroj && formik.errors.invbroj},
                                                {'is-valid': formik.touched.invbroj && !formik.errors.invbroj}
                                            )}
                                        />
                                    </div>

                                    {formik.touched.invbroj && formik.errors.invbroj && (
                                        <div className='fv-plugins-message-container mt-2'>
                                            <div className='fv-help-block'>{formik.errors.invbroj as any}</div>
                                        </div>
                                    )}

                                    {/* Sredstvo info label */}
                                    {sredstvoInfoData?.SredstvoNaziv ? (
                                        <div className='mt-3'>
                                            <div className='alert alert-success d-flex align-items-center py-3 mb-0'>
                                                <KTIcon iconName='verify' className='fs-2 me-2' />
                                                <div className='fw-semibold'>{sredstvoInfoData.SredstvoNaziv}</div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <hr className='my-6' />

                            {/* Izvršioci */}
                            <div className='d-flex flex-column gap-2 mb-4'>
                                <label className='form-label fw-bold mb-0'>
                                    Ostali Izvršioci{' '}
                                    <span className='text-muted fw-normal d-block mt-1'>
                    Voditelj izrade naloga (onaj koji upisuje) ne upisuje sebe, on je automatski upisan.
                  </span>
                                </label>
                            </div>

                            <div className='d-flex flex-column gap-3'>
                                {Array.from({length: formik.values.Izvrsilac!.length}, (_, i) => (
                                    <div
                                        key={i}
                                        className='d-flex flex-column flex-md-row align-items-stretch align-items-md-end gap-3'
                                    >
                                        <div className='flex-grow-1'>
                                            <label className='form-label text-muted mb-2'>Izvršilac #{i + 1}</label>
                                            <UposlenikDropdown
                                                value={formik.values.Izvrsilac![i]}
                                                onChange={(name, value) => {
                                                    const al = [...(formik.values.Izvrsilac ?? [])]
                                                    al.splice(i, 1, value)
                                                    formik.setFieldValue('Izvrsilac', al)
                                                }}
                                                touched={formik.touched.Izvrsilac as any}
                                                selectName='Izvrsilac'
                                            />
                                        </div>

                                        <div className='d-flex gap-2'>
                                            <button
                                                type='button'
                                                className='btn btn-light-danger d-flex align-items-center justify-content-center'
                                                style={{minWidth: 48}}
                                                onClick={() => {
                                                    const al = [...(formik.values.Izvrsilac ?? [])]
                                                    al.splice(i, 1)
                                                    formik.setFieldValue('Izvrsilac', al.length ? al : [''])
                                                }}
                                                title='Ukloni'
                                            >
                                                <KTIcon iconName='minus' className='fs-2' />
                                            </button>

                                            {/* On mobile it's nicer if add button is near list; keep it below too, but this is fine */}
                                        </div>
                                    </div>
                                ))}

                                <div className='d-flex justify-content-start'>
                                    <button
                                        type='button'
                                        className='btn btn-primary d-inline-flex align-items-center gap-2'
                                        onClick={() => {
                                            const al = [...(formik.values.Izvrsilac ?? [])]
                                            al.push('')
                                            formik.setFieldValue('Izvrsilac', al)
                                        }}
                                    >
                                        <KTIcon iconName='plus' className='fs-2' />
                                        Dodaj izvršioca
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </div>
    )
})

export default RadniNalogComponent
